import { ErrorMessage } from '@atlaskit/form';
import { Flex, Stack, Text } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import { useIntl } from 'react-intl-next';

import type { RepositorySettingValue } from '../../../../../../../typings/globals';
import { useRovoDevWebAnalyticsClient } from '../../../../../controllers/rovo-dev-web-analytics-events';
import { useToggleCodeReviewerSetting } from '../../hooks/use-toggle-code-reviewer-setting';
import { CodeReviewerSettingTooltip } from '../code-reviewer-setting-tooltip';
import messages from '../messages';

type RepositoryPageToggleProps = {
	repository: GH.Repository;
	updateRepoSettings: (update: RepositorySettingValue) => void;
};
export const CodeReviewerRepositorySettingToggle = ({
	repository,
	updateRepoSettings,
}: RepositoryPageToggleProps) => {
	const { formatMessage } = useIntl();
	const onToggle = (newValue: boolean) => {
		const update = {
			autoreview_acceptance_criteria_commit_trigger_enabled:
				repository.autoreviewSettings?.value
					.autoreview_acceptance_criteria_commit_trigger_enabled ?? true,
			autoreview_acceptance_criteria_enabled:
				repository.autoreviewSettings?.value.autoreview_acceptance_criteria_enabled ?? true,
			autoreview_enabled: newValue,
		};

		updateRepoSettings(update);
	};

	const { isEnabled, updateSetting, error } = useToggleCodeReviewerSetting({
		entity: repository,
		onToggle,
	});
	const { fireUiEvent } = useRovoDevWebAnalyticsClient();

	const handleToggle = () => {
		const newValue = !isEnabled;
		updateSetting(newValue);
		fireUiEvent({
			action: 'clicked',
			actionSubject: `code-reviewer-repository-toggle-${newValue ? 'on' : 'off'}`,
			source: 'code-reviewer-repository-settings',
		});
	};

	return (
		<Stack space="space.100">
			<Flex alignItems="center" gap="space.050">
				<Toggle
					testId="settings-page-toggle"
					size="large"
					label=""
					onChange={handleToggle}
					isChecked={isEnabled}
				/>
				<Text weight="bold">{formatMessage(messages.reviewPullRequestsToggleLabel)}</Text>
				<CodeReviewerSettingTooltip
					content={formatMessage(messages.reviewPullRequestsTooltip)}
					label={formatMessage(messages.reviewPullRequestsTooltipLabel)}
				/>
			</Flex>

			{error && (
				<ErrorMessage testId="code-reviewer-repository-settings-error">
					{formatMessage(error)}
				</ErrorMessage>
			)}
		</Stack>
	);
};
